import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import type {
  Company, HiringContact, ContactStatus, CompanyStatus, HistoryEntry, AssignableEmployee, CompanyAssignee, ContactAssignee,
  CleanupPreview, BulkAssignResult,
} from "../api/HiringContactsApi";
import {
  fetchCompanies, fetchContacts, fetchAssignments, fetchAssignmentStatusCounts,
  fetchAssignableEmployees, assignContact, unassignContact, updateAssignmentStatus,
  fetchAssignmentHistory, setCompanyWatch, setContactWatch, setCompanyPriority, setContactPriority,
  createCompany, createContact,
  assignCompany, unassignCompany, fetchCompanyAssignees, fetchContactAssignees,
  fetchCompanyAssignments, fetchCompanyAssignmentStatusCounts, fetchCompanyAssignedDistinctCount, updateCompanyAssignmentStatus,
  fetchCompanyAssignmentHistory,
  archiveCompany, restoreCompany, deleteCompany, archiveContact, restoreContact, deleteContact,
  fetchArchivedCompaniesPreview, deleteArchivedCompanies, fetchArchivedContactsPreview, deleteArchivedContacts,
} from "../api/HiringContactsApi";

export interface HiringContactsContextType {
  isAdmin: boolean;

  // Assignments tab
  assignments: HiringContact[];
  assignmentsTotalCount: number;
  assignmentsTotalPages: number;
  assignmentsPage: number;
  assignmentsLoading: boolean;
  assignmentsLoadError: string | null;
  statusFilter: string;
  statusCounts: Record<string, number>;
  selectedManagerId: number | null;
  setStatusFilter: (v: string) => void;
  setSelectedManagerId: (v: number | null) => void;
  loadAssignments: (pg?: number) => Promise<void>;

  // Watch filter — shared across the merged Contacts tab (Assigned + Unassigned scopes)
  contactsWatchFilter: string;
  setContactsWatchFilter: (v: string) => void;

  // Unfiltered totals for the Assigned/Unassigned scope tab labels (admin only) —
  // "Assigned" is the sum of statusCounts (already filter-independent); this is its
  // Unassigned counterpart, kept in sync the same way.
  unassignedCount: number;

  // Same idea for the Companies merged tab's "Unassigned" scope label — kept in sync
  // independently of whichever scope is currently active so the badge is accurate
  // before that scope is ever visited.
  unassignedCompaniesCount: number;

  // Companies merged tab's "Assigned" scope label — a distinct-company count, NOT
  // the sum of companyStatusCounts (which is per-assignment-row and double-counts
  // a company assigned to more than one TM).
  assignedCompaniesCount: number;

  // Companies tab (admin only — browse unassigned + assign)
  companies: Company[];
  companiesTotalCount: number;
  companiesTotalPages: number;
  companiesPage: number;
  companiesLoading: boolean;
  companiesSearchInput: string;
  setCompaniesSearchInput: (v: string) => void;
  handleCompaniesSearchSubmit: (e: React.FormEvent) => void;
  loadCompanies: (pg?: number) => Promise<void>;
  companiesWatchFilter: string;
  setCompaniesWatchFilter: (v: string) => void;
  handleCompanyWatchToggle: (companyId: number, isWatched: boolean) => Promise<void>;
  handleCompanyPriorityChange: (companyId: number, priority: "high" | "medium" | "low" | null) => Promise<void>;
  handleArchiveCompany: (companyId: number) => Promise<void>;
  companyActionError: string | null;
  handleCreateCompany: (name: string, domain: string, linkedinUrl: string) => Promise<Company>;
  companyAssignees: CompanyAssignee[];
  loadCompanyAssignees: (companyId: number) => Promise<void>;
  handleAssignCompany: (companyId: number, employeeIds: number[]) => Promise<BulkAssignResult>;
  handleUnassignCompany: (assignmentId: number, companyId: number) => Promise<void>;

  // Companies tab — drill-down (expand a company row to see its contacts inline)
  expandedCompanyId: number | null;
  expandedCompanyContacts: HiringContact[];
  expandedCompanyContactsLoading: boolean;
  toggleCompanyExpand: (companyId: number) => Promise<void>;

  // Contacts tab (admin only — browse unassigned + assign)
  contacts: HiringContact[];
  contactsTotalCount: number;
  contactsTotalPages: number;
  contactsPage: number;
  contactsLoading: boolean;
  contactsSearchInput: string;
  setContactsSearchInput: (v: string) => void;
  handleContactsSearchSubmit: (e: React.FormEvent) => void;
  loadContacts: (pg?: number) => Promise<void>;
  handleContactWatchToggle: (contactId: number, isWatched: boolean) => Promise<void>;
  handleContactPriorityChange: (contactId: number, priority: "high" | "medium" | "low" | null) => Promise<void>;
  handleArchiveContact: (contactId: number) => Promise<void>;
  contactActionError: string | null;
  handleCreateContact: (fullName: string, linkedinUrl: string, companyId: number) => Promise<HiringContact>;

  // Archived scope (admin only) — companies/contacts nobody should be
  // sourcing/tracking anymore. Restore brings a row back; delete is only
  // permitted here (an archived row), matching the archive-first convention.
  archivedCompanies: Company[];
  archivedCompaniesTotalCount: number;
  archivedCompaniesTotalPages: number;
  archivedCompaniesPage: number;
  archivedCompaniesLoading: boolean;
  archivedCompaniesCount: number;
  loadArchivedCompanies: (pg?: number) => Promise<void>;
  handleRestoreCompany: (companyId: number) => Promise<void>;
  handleDeleteCompany: (companyId: number) => Promise<void>;
  companyCleanupModal: { days: number; preview: CleanupPreview } | null;
  companyCleanupExecuting: boolean;
  companyCleanupError: string | null;
  openCompanyCleanup: (days: number) => Promise<void>;
  executeCompanyCleanup: () => Promise<void>;
  closeCompanyCleanupModal: () => void;

  archivedContacts: HiringContact[];
  archivedContactsTotalCount: number;
  archivedContactsTotalPages: number;
  archivedContactsPage: number;
  archivedContactsLoading: boolean;
  archivedContactsCount: number;
  loadArchivedContacts: (pg?: number) => Promise<void>;
  handleRestoreContact: (contactId: number) => Promise<void>;
  handleDeleteContact: (contactId: number) => Promise<void>;
  contactCleanupModal: { days: number; preview: CleanupPreview } | null;
  contactCleanupExecuting: boolean;
  contactCleanupError: string | null;
  openContactCleanup: (days: number) => Promise<void>;
  executeContactCleanup: () => Promise<void>;
  closeContactCleanupModal: () => void;

  assignableEmployees: AssignableEmployee[];
  contactAssignees: ContactAssignee[];
  loadContactAssignees: (contactId: number) => Promise<void>;
  handleAssign: (contactId: number, employeeIds: number[]) => Promise<BulkAssignResult>;
  handleUnassign: (assignmentId: number, contactId?: number) => Promise<void>;

  // Status update + history
  handleStatusUpdate: (assignmentId: number, status: ContactStatus, notes?: string | null) => Promise<void>;
  loadHistory: (assignmentId: number) => Promise<void>;
  history: HistoryEntry[];
  historyLoading: boolean;

  // Company Assignments tab (My Companies / admin Companies-Assigned scope)
  companyAssignments: Company[];
  companyAssignmentsTotalCount: number;
  companyAssignmentsTotalPages: number;
  companyAssignmentsPage: number;
  companyAssignmentsLoading: boolean;
  companyAssignmentsLoadError: string | null;
  companyStatusFilter: string;
  companyStatusCounts: Record<string, number>;
  setCompanyStatusFilter: (v: string) => void;
  loadCompanyAssignments: (pg?: number) => Promise<void>;
  handleCompanyStatusUpdate: (assignmentId: number, status: CompanyStatus, notes?: string | null) => Promise<void>;
  loadCompanyHistory: (assignmentId: number) => Promise<void>;
  companyHistory: HistoryEntry[];
  companyHistoryLoading: boolean;
}

const HiringContactsContext = createContext<HiringContactsContextType | undefined>(undefined);

export function useHiringContacts(): HiringContactsContextType {
  const ctx = useContext(HiringContactsContext);
  if (!ctx) throw new Error("useHiringContacts must be used inside HiringContactsProvider");
  return ctx;
}

interface Props {
  isAdmin: boolean;
  children: ReactNode;
}

export function HiringContactsProvider({ isAdmin, children }: Props) {
  const [assignments, setAssignments] = useState<HiringContact[]>([]);
  const [assignmentsTotalCount, setAssignmentsTotalCount] = useState(0);
  const [assignmentsTotalPages, setAssignmentsTotalPages] = useState(1);
  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [assignmentsLoadError, setAssignmentsLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);
  const [contactsWatchFilter, setContactsWatchFilter] = useState("");
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [unassignedCompaniesCount, setUnassignedCompaniesCount] = useState(0);
  const [assignedCompaniesCount, setAssignedCompaniesCount] = useState(0);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesWatchFilter, setCompaniesWatchFilter] = useState("");
  const [companiesTotalCount, setCompaniesTotalCount] = useState(0);
  const [companiesTotalPages, setCompaniesTotalPages] = useState(1);
  const [companiesPage, setCompaniesPage] = useState(1);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  // Raw input (updates every keystroke, drives the controlled field) is kept separate
  // from the committed search term (only updates on submit, drives the actual fetch) —
  // otherwise every keystroke would fire a new request and out-of-order responses
  // could overwrite newer results with a stale, broader result set.
  const [companiesSearchInput, setCompaniesSearchInput] = useState("");
  const [companiesSearch, setCompaniesSearch] = useState("");

  const [expandedCompanyId, setExpandedCompanyId] = useState<number | null>(null);
  const [expandedCompanyContacts, setExpandedCompanyContacts] = useState<HiringContact[]>([]);
  const [expandedCompanyContactsLoading, setExpandedCompanyContactsLoading] = useState(false);

  const [contacts, setContacts] = useState<HiringContact[]>([]);
  const [contactsTotalCount, setContactsTotalCount] = useState(0);
  const [contactsTotalPages, setContactsTotalPages] = useState(1);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsSearchInput, setContactsSearchInput] = useState("");
  const [contactsSearch, setContactsSearch] = useState("");

  const [companyActionError, setCompanyActionError] = useState<string | null>(null);
  const [contactActionError, setContactActionError] = useState<string | null>(null);

  const [archivedCompanies, setArchivedCompanies] = useState<Company[]>([]);
  const [archivedCompaniesTotalCount, setArchivedCompaniesTotalCount] = useState(0);
  const [archivedCompaniesTotalPages, setArchivedCompaniesTotalPages] = useState(1);
  const [archivedCompaniesPage, setArchivedCompaniesPage] = useState(1);
  const [archivedCompaniesLoading, setArchivedCompaniesLoading] = useState(false);
  const [archivedCompaniesCount, setArchivedCompaniesCount] = useState(0);
  const [companyCleanupModal, setCompanyCleanupModal] = useState<{ days: number; preview: CleanupPreview } | null>(null);
  const [companyCleanupExecuting, setCompanyCleanupExecuting] = useState(false);
  const [companyCleanupError, setCompanyCleanupError] = useState<string | null>(null);

  const [archivedContacts, setArchivedContacts] = useState<HiringContact[]>([]);
  const [archivedContactsTotalCount, setArchivedContactsTotalCount] = useState(0);
  const [archivedContactsTotalPages, setArchivedContactsTotalPages] = useState(1);
  const [archivedContactsPage, setArchivedContactsPage] = useState(1);
  const [archivedContactsLoading, setArchivedContactsLoading] = useState(false);
  const [archivedContactsCount, setArchivedContactsCount] = useState(0);
  const [contactCleanupModal, setContactCleanupModal] = useState<{ days: number; preview: CleanupPreview } | null>(null);
  const [contactCleanupExecuting, setContactCleanupExecuting] = useState(false);
  const [contactCleanupError, setContactCleanupError] = useState<string | null>(null);

  const [assignableEmployees, setAssignableEmployees] = useState<AssignableEmployee[]>([]);
  const [companyAssignees, setCompanyAssignees] = useState<CompanyAssignee[]>([]);
  const [contactAssignees, setContactAssignees] = useState<ContactAssignee[]>([]);

  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [companyAssignments, setCompanyAssignments] = useState<Company[]>([]);
  const [companyAssignmentsTotalCount, setCompanyAssignmentsTotalCount] = useState(0);
  const [companyAssignmentsTotalPages, setCompanyAssignmentsTotalPages] = useState(1);
  const [companyAssignmentsPage, setCompanyAssignmentsPage] = useState(1);
  const [companyAssignmentsLoading, setCompanyAssignmentsLoading] = useState(true);
  const [companyAssignmentsLoadError, setCompanyAssignmentsLoadError] = useState<string | null>(null);
  const [companyStatusFilter, setCompanyStatusFilter] = useState("");
  const [companyStatusCounts, setCompanyStatusCounts] = useState<Record<string, number>>({});
  const [companyHistory, setCompanyHistory] = useState<HistoryEntry[]>([]);
  const [companyHistoryLoading, setCompanyHistoryLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchAssignableEmployees().then(setAssignableEmployees).catch(() => setAssignableEmployees([]));
    }
  }, [isAdmin]);

  const refreshStatusCounts = useCallback(async () => {
    try {
      setStatusCounts(await fetchAssignmentStatusCounts(
        isAdmin,
        isAdmin && selectedManagerId ? selectedManagerId : undefined,
        contactsWatchFilter ? contactsWatchFilter === "true" : undefined,
      ));
    } catch {
      setStatusCounts({});
    }
  }, [isAdmin, selectedManagerId, contactsWatchFilter]);

  const refreshUnassignedCount = useCallback(async () => {
    try {
      const result = await fetchContacts({
        page: 1,
        page_size: 1,
        assigned_only: false,
        is_watched: contactsWatchFilter ? contactsWatchFilter === "true" : undefined,
      });
      setUnassignedCount(result.total_count);
    } catch {
      setUnassignedCount(0);
    }
  }, [contactsWatchFilter]);

  useEffect(() => { refreshStatusCounts(); }, [refreshStatusCounts]);

  useEffect(() => {
    if (isAdmin) refreshUnassignedCount();
  }, [isAdmin, refreshUnassignedCount]);

  const refreshCompanyStatusCounts = useCallback(async () => {
    try {
      setCompanyStatusCounts(await fetchCompanyAssignmentStatusCounts(
        isAdmin,
        isAdmin && selectedManagerId ? selectedManagerId : undefined,
        companiesWatchFilter ? companiesWatchFilter === "true" : undefined,
      ));
    } catch {
      setCompanyStatusCounts({});
    }
  }, [isAdmin, selectedManagerId, companiesWatchFilter]);

  useEffect(() => { refreshCompanyStatusCounts(); }, [refreshCompanyStatusCounts]);

  const refreshUnassignedCompaniesCount = useCallback(async () => {
    try {
      const result = await fetchCompanies({
        page: 1,
        page_size: 1,
        assigned_only: false,
        is_watched: companiesWatchFilter ? companiesWatchFilter === "true" : undefined,
      });
      setUnassignedCompaniesCount(result.total_count);
    } catch {
      setUnassignedCompaniesCount(0);
    }
  }, [companiesWatchFilter]);

  useEffect(() => {
    if (isAdmin) refreshUnassignedCompaniesCount();
  }, [isAdmin, refreshUnassignedCompaniesCount]);

  const refreshAssignedCompaniesCount = useCallback(async () => {
    try {
      setAssignedCompaniesCount(await fetchCompanyAssignedDistinctCount(
        companiesWatchFilter ? companiesWatchFilter === "true" : undefined,
      ));
    } catch {
      setAssignedCompaniesCount(0);
    }
  }, [companiesWatchFilter]);

  useEffect(() => {
    if (isAdmin) refreshAssignedCompaniesCount();
  }, [isAdmin, refreshAssignedCompaniesCount]);

  const refreshArchivedCompaniesCount = useCallback(async () => {
    try {
      const result = await fetchCompanies({ page: 1, page_size: 1, is_active: false });
      setArchivedCompaniesCount(result.total_count);
    } catch {
      setArchivedCompaniesCount(0);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) refreshArchivedCompaniesCount();
  }, [isAdmin, refreshArchivedCompaniesCount]);

  const refreshArchivedContactsCount = useCallback(async () => {
    try {
      const result = await fetchContacts({ page: 1, page_size: 1, is_active: false });
      setArchivedContactsCount(result.total_count);
    } catch {
      setArchivedContactsCount(0);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) refreshArchivedContactsCount();
  }, [isAdmin, refreshArchivedContactsCount]);

  const loadAssignments = useCallback(async (pg = 1) => {
    setAssignmentsLoading(true);
    setAssignmentsLoadError(null);
    if (pg === 1) setAssignments([]);
    try {
      const result = await fetchAssignments(
        {
          page: pg,
          page_size: 20,
          status: statusFilter || undefined,
          hide_archived: statusFilter !== "archived",
          employee_id: isAdmin && selectedManagerId ? selectedManagerId : undefined,
          is_watched: contactsWatchFilter ? contactsWatchFilter === "true" : undefined,
        },
        isAdmin,
      );
      setAssignments(result.items);
      setAssignmentsTotalCount(result.total_count);
      setAssignmentsTotalPages(result.total_pages);
      setAssignmentsPage(pg);
    } catch {
      setAssignmentsLoadError("Failed to load contacts. Please refresh and try again.");
    } finally {
      setAssignmentsLoading(false);
    }
  }, [isAdmin, statusFilter, selectedManagerId, contactsWatchFilter]);

  const loadCompanyAssignments = useCallback(async (pg = 1) => {
    setCompanyAssignmentsLoading(true);
    setCompanyAssignmentsLoadError(null);
    if (pg === 1) setCompanyAssignments([]);
    try {
      const result = await fetchCompanyAssignments(
        {
          page: pg,
          page_size: 20,
          status: companyStatusFilter || undefined,
          hide_archived: companyStatusFilter !== "archived",
          employee_id: isAdmin && selectedManagerId ? selectedManagerId : undefined,
          is_watched: companiesWatchFilter ? companiesWatchFilter === "true" : undefined,
        },
        isAdmin,
      );
      setCompanyAssignments(result.items);
      setCompanyAssignmentsTotalCount(result.total_count);
      setCompanyAssignmentsTotalPages(result.total_pages);
      setCompanyAssignmentsPage(pg);
    } catch {
      setCompanyAssignmentsLoadError("Failed to load companies. Please refresh and try again.");
    } finally {
      setCompanyAssignmentsLoading(false);
    }
  }, [isAdmin, companyStatusFilter, selectedManagerId, companiesWatchFilter]);

  // Only ever used for the "Unassigned" scope of the merged Companies tab now —
  // always scoped to companies nobody has picked up yet.
  const loadCompanies = useCallback(async (pg = 1) => {
    setCompaniesLoading(true);
    try {
      const result = await fetchCompanies({
        page: pg,
        page_size: 20,
        search: companiesSearch || undefined,
        assigned_only: false,
        is_watched: companiesWatchFilter ? companiesWatchFilter === "true" : undefined,
      });
      setCompanies(result.items);
      setCompaniesTotalCount(result.total_count);
      setCompaniesTotalPages(result.total_pages);
      setCompaniesPage(pg);
    } catch {
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  }, [companiesSearch, companiesWatchFilter]);

  function handleCompaniesSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setCompaniesSearch(companiesSearchInput);
  }

  async function handleCompanyWatchToggle(companyId: number, isWatched: boolean) {
    const updated = await setCompanyWatch(companyId, isWatched);
    setCompanies((prev) => prev.map((c) => (c.id === companyId ? updated : c)));
    // companyAssignments items carry assignment-overlay fields (status, assignment_id,
    // assigned_employees) that `updated` (a bare Company) doesn't have — patch just the
    // changed field instead of replacing the whole object, or the card/modal currently
    // open on this company would lose its tracking data.
    setCompanyAssignments((prev) => prev.map((c) => (c.id === companyId ? { ...c, is_watched: updated.is_watched } : c)));
  }

  async function handleCompanyPriorityChange(companyId: number, priority: "high" | "medium" | "low" | null) {
    const updated = await setCompanyPriority(companyId, priority);
    setCompanies((prev) => prev.map((c) => (c.id === companyId ? updated : c)));
    setCompanyAssignments((prev) => prev.map((c) => (c.id === companyId ? { ...c, priority: updated.priority } : c)));
  }

  async function handleArchiveCompany(companyId: number) {
    setCompanyActionError(null);
    try {
      await archiveCompany(companyId);
    } catch {
      setCompanyActionError("Failed to archive company. Please try again.");
      return;
    }
    // Archiving cascades to the company's contacts server-side, so any of the
    // contact-side lists could now be stale too — refresh everything affected.
    // This includes the Contacts "Assigned" scope list (`assignments`), not just
    // the Unassigned browse list (`contacts`) — a cascade-archived contact could
    // be sitting in either one, or both (different TMs), depending on who's
    // assigned to it. Reloading (not locally filtering) also keeps
    // totalCount/totalPages accurate for pagination.
    await Promise.all([
      loadCompanies(companiesPage),
      loadCompanyAssignments(companyAssignmentsPage),
      refreshUnassignedCompaniesCount(),
      isAdmin ? refreshAssignedCompaniesCount() : Promise.resolve(),
      isAdmin ? refreshArchivedCompaniesCount() : Promise.resolve(),
      refreshCompanyStatusCounts(),
      loadContacts(contactsPage),
      loadAssignments(assignmentsPage),
      refreshUnassignedCount(),
      refreshStatusCounts(),
      isAdmin ? refreshArchivedContactsCount() : Promise.resolve(),
    ]);
  }

  const loadArchivedCompanies = useCallback(async (pg = 1) => {
    setArchivedCompaniesLoading(true);
    try {
      const result = await fetchCompanies({ page: pg, page_size: 20, is_active: false });
      setArchivedCompanies(result.items);
      setArchivedCompaniesTotalCount(result.total_count);
      setArchivedCompaniesTotalPages(result.total_pages);
      setArchivedCompaniesPage(pg);
    } catch {
      setArchivedCompanies([]);
    } finally {
      setArchivedCompaniesLoading(false);
    }
  }, []);

  async function handleRestoreCompany(companyId: number) {
    setCompanyActionError(null);
    try {
      await restoreCompany(companyId);
    } catch {
      setCompanyActionError("Failed to restore company. Please try again.");
      return;
    }
    await Promise.all([
      loadArchivedCompanies(archivedCompaniesPage),
      refreshArchivedCompaniesCount(),
      refreshUnassignedCompaniesCount(),
    ]);
  }

  async function handleDeleteCompany(companyId: number) {
    await deleteCompany(companyId);
    await Promise.all([loadArchivedCompanies(archivedCompaniesPage), refreshArchivedCompaniesCount()]);
  }

  async function openCompanyCleanup(days: number) {
    setCompanyCleanupError(null);
    try {
      const preview = await fetchArchivedCompaniesPreview(days);
      setCompanyCleanupModal({ days, preview });
    } catch {
      setCompanyCleanupError("Failed to load cleanup preview. Please try again.");
    }
  }

  async function executeCompanyCleanup() {
    if (!companyCleanupModal) return;
    setCompanyCleanupExecuting(true);
    setCompanyCleanupError(null);
    try {
      await deleteArchivedCompanies(companyCleanupModal.days);
      setCompanyCleanupModal(null);
      await Promise.all([loadArchivedCompanies(1), refreshArchivedCompaniesCount()]);
    } catch {
      setCompanyCleanupError("Cleanup failed. Please try again.");
    } finally {
      setCompanyCleanupExecuting(false);
    }
  }

  function closeCompanyCleanupModal() {
    setCompanyCleanupModal(null);
    setCompanyCleanupError(null);
  }

  async function handleCreateCompany(name: string, domain: string, linkedinUrl: string) {
    const company = await createCompany(name, domain, linkedinUrl, isAdmin);
    if (isAdmin) {
      // Admin-created companies aren't self-assigned — refresh the browse list.
      await loadCompanies(1);
      await refreshUnassignedCompaniesCount();
    } else {
      // Employee-created companies are auto-assigned to the creator — refresh
      // "My Companies" (and its status counts) so it shows up immediately.
      await loadCompanyAssignments(1);
      await refreshCompanyStatusCounts();
    }
    return company;
  }

  async function loadCompanyAssignees(companyId: number) {
    try {
      setCompanyAssignees(await fetchCompanyAssignees(companyId));
    } catch {
      setCompanyAssignees([]);
    }
  }

  async function handleAssignCompany(companyId: number, employeeIds: number[]) {
    const result = await assignCompany(companyId, employeeIds);
    // None of these reads depend on each other's response — only on the write
    // above having completed — so run them concurrently instead of one at a time.
    await Promise.all([
      loadCompanyAssignees(companyId),
      loadCompanies(companiesPage),
      loadCompanyAssignments(1),
      refreshCompanyStatusCounts(),
      refreshUnassignedCompaniesCount(),
      isAdmin ? refreshAssignedCompaniesCount() : Promise.resolve(),
    ]);
    return result;
  }

  async function handleUnassignCompany(assignmentId: number, companyId: number) {
    await unassignCompany(assignmentId);
    setCompanyAssignments((prev) => prev.filter((c) => c.assignment_id !== assignmentId));
    await Promise.all([
      loadCompanyAssignees(companyId),
      loadCompanies(companiesPage),
      loadCompanyAssignments(companyAssignmentsPage),
      refreshCompanyStatusCounts(),
      refreshUnassignedCompaniesCount(),
      isAdmin ? refreshAssignedCompaniesCount() : Promise.resolve(),
    ]);
  }

  async function refreshExpandedCompanyContacts(companyId: number) {
    setExpandedCompanyContactsLoading(true);
    try {
      const result = await fetchContacts({ company_id: companyId, page_size: 50 });
      setExpandedCompanyContacts(result.items);
    } catch {
      setExpandedCompanyContacts([]);
    } finally {
      setExpandedCompanyContactsLoading(false);
    }
  }

  async function toggleCompanyExpand(companyId: number) {
    if (expandedCompanyId === companyId) {
      setExpandedCompanyId(null);
      setExpandedCompanyContacts([]);
      return;
    }
    setExpandedCompanyId(companyId);
    await refreshExpandedCompanyContacts(companyId);
  }

  // This data source is only ever used for the "Unassigned" side of the merged
  // Contacts tab now — always scoped to contacts nobody has picked up yet.
  const loadContacts = useCallback(async (pg = 1) => {
    setContactsLoading(true);
    try {
      const result = await fetchContacts({
        page: pg,
        page_size: 20,
        search: contactsSearch || undefined,
        assigned_only: false,
        is_watched: contactsWatchFilter ? contactsWatchFilter === "true" : undefined,
      });
      setContacts(result.items);
      setContactsTotalCount(result.total_count);
      setContactsTotalPages(result.total_pages);
      setContactsPage(pg);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [contactsSearch, contactsWatchFilter]);

  function handleContactsSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setContactsSearch(contactsSearchInput);
  }

  async function handleContactWatchToggle(contactId: number, isWatched: boolean) {
    const updated = await setContactWatch(contactId, isWatched);
    setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updated } : c)));
    setAssignments((prev) => prev.map((c) => (c.id === contactId ? { ...c, is_watched: updated.is_watched } : c)));
    setExpandedCompanyContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updated } : c)));
  }

  async function handleContactPriorityChange(contactId: number, priority: "high" | "medium" | "low" | null) {
    const updated = await setContactPriority(contactId, priority);
    setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updated } : c)));
    setAssignments((prev) => prev.map((c) => (c.id === contactId ? { ...c, priority: updated.priority } : c)));
    setExpandedCompanyContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updated } : c)));
  }

  async function handleArchiveContact(contactId: number) {
    setContactActionError(null);
    try {
      await archiveContact(contactId);
    } catch {
      setContactActionError("Failed to archive contact. Please try again.");
      return;
    }
    // Reloading (not locally filtering) keeps totalCount/totalPages accurate
    // for pagination, matching the pattern already used by handleAssign/handleUnassign.
    await Promise.all([
      loadContacts(contactsPage),
      loadAssignments(assignmentsPage),
      refreshUnassignedCount(),
      refreshStatusCounts(),
      isAdmin ? refreshArchivedContactsCount() : Promise.resolve(),
      expandedCompanyId !== null ? refreshExpandedCompanyContacts(expandedCompanyId) : Promise.resolve(),
    ]);
  }

  const loadArchivedContacts = useCallback(async (pg = 1) => {
    setArchivedContactsLoading(true);
    try {
      const result = await fetchContacts({ page: pg, page_size: 20, is_active: false });
      setArchivedContacts(result.items);
      setArchivedContactsTotalCount(result.total_count);
      setArchivedContactsTotalPages(result.total_pages);
      setArchivedContactsPage(pg);
    } catch {
      setArchivedContacts([]);
    } finally {
      setArchivedContactsLoading(false);
    }
  }, []);

  async function handleRestoreContact(contactId: number) {
    setContactActionError(null);
    try {
      await restoreContact(contactId);
    } catch (err: any) {
      // restore_contact can 422 with a specific "restore the company first"
      // message when the parent company is still archived — surface it verbatim
      // instead of a generic failure, since it tells the admin exactly what to do.
      setContactActionError(err?.response?.data?.detail || "Failed to restore contact. Please try again.");
      return;
    }
    await Promise.all([
      loadArchivedContacts(archivedContactsPage),
      refreshArchivedContactsCount(),
      refreshUnassignedCount(),
    ]);
  }

  async function handleDeleteContact(contactId: number) {
    await deleteContact(contactId);
    await Promise.all([loadArchivedContacts(archivedContactsPage), refreshArchivedContactsCount()]);
  }

  async function openContactCleanup(days: number) {
    setContactCleanupError(null);
    try {
      const preview = await fetchArchivedContactsPreview(days);
      setContactCleanupModal({ days, preview });
    } catch {
      setContactCleanupError("Failed to load cleanup preview. Please try again.");
    }
  }

  async function executeContactCleanup() {
    if (!contactCleanupModal) return;
    setContactCleanupExecuting(true);
    setContactCleanupError(null);
    try {
      await deleteArchivedContacts(contactCleanupModal.days);
      setContactCleanupModal(null);
      await Promise.all([loadArchivedContacts(1), refreshArchivedContactsCount()]);
    } catch {
      setContactCleanupError("Cleanup failed. Please try again.");
    } finally {
      setContactCleanupExecuting(false);
    }
  }

  function closeContactCleanupModal() {
    setContactCleanupModal(null);
    setContactCleanupError(null);
  }

  async function handleCreateContact(fullName: string, linkedinUrl: string, companyId: number) {
    const contact = await createContact(fullName, linkedinUrl, companyId, isAdmin);
    if (isAdmin) {
      // Admin-created contacts start unassigned — refresh the Unassigned list/count.
      await loadContacts(1);
      await refreshUnassignedCount();
    } else {
      // Employee-created contacts are auto-assigned to the creator — refresh "My
      // Contacts" (and its status counts) so the new contact shows up immediately.
      await loadAssignments(1);
      await refreshStatusCounts();
    }
    return contact;
  }

  async function loadContactAssignees(contactId: number) {
    try {
      setContactAssignees(await fetchContactAssignees(contactId));
    } catch {
      setContactAssignees([]);
    }
  }

  async function handleAssign(contactId: number, employeeIds: number[]) {
    const result = await assignContact(contactId, employeeIds);
    // None of these reads depend on each other's response — only on the write
    // above having completed — so run them concurrently instead of one at a time.
    await Promise.all([
      loadContactAssignees(contactId),
      loadContacts(contactsPage),
      loadAssignments(1),
      refreshStatusCounts(),
      refreshUnassignedCount(),
      expandedCompanyId !== null ? refreshExpandedCompanyContacts(expandedCompanyId) : Promise.resolve(),
    ]);
    return result;
  }

  async function handleUnassign(assignmentId: number, contactId?: number) {
    await unassignContact(assignmentId);
    // A full reload (not just filtering the removed row out) matters here: every
    // surviving row for the same contact carries its own snapshot of the full
    // assigned_employees roster, so a local filter would leave the removed TM's
    // chip stuck on the sibling row(s) until the next reload.
    setAssignments((prev) => prev.filter((c) => c.assignment_id !== assignmentId));
    await Promise.all([
      contactId !== undefined ? loadContactAssignees(contactId) : Promise.resolve(),
      loadContacts(contactsPage),
      loadAssignments(assignmentsPage),
      refreshStatusCounts(),
      refreshUnassignedCount(),
      expandedCompanyId !== null ? refreshExpandedCompanyContacts(expandedCompanyId) : Promise.resolve(),
    ]);
  }

  async function handleStatusUpdate(assignmentId: number, status: ContactStatus, notes?: string | null) {
    await updateAssignmentStatus(assignmentId, status, notes, isAdmin);

    // Every row (this one, plus any sibling row for the same contact under a
    // different TM) carries its own snapshot of the full assigned_employees
    // roster — patch the matching entry everywhere it appears, or the chip for
    // this TM would keep showing the old status until an unrelated reload.
    const patchRosterEntry = (c: HiringContact): HiringContact =>
      c.assigned_employees?.some((e) => e.assignment_id === assignmentId)
        ? { ...c, assigned_employees: c.assigned_employees.map((e) => (e.assignment_id === assignmentId ? { ...e, status } : e)) }
        : c;

    setAssignments((prev) =>
      prev.map((c) => {
        if (c.assignment_id !== assignmentId) return patchRosterEntry(c);
        return patchRosterEntry({
          ...c,
          status,
          assignment_notes: notes !== undefined ? notes : c.assignment_notes,
          assignment_from_status: c.status ?? c.assignment_from_status,
        });
      })
    );
    setContacts((prev) => prev.map(patchRosterEntry));
    setExpandedCompanyContacts((prev) => prev.map(patchRosterEntry));

    try { await loadHistory(assignmentId); } catch {}
    refreshStatusCounts();
  }

  async function loadHistory(assignmentId: number) {
    setHistoryLoading(true);
    try {
      const h = await fetchAssignmentHistory(assignmentId, isAdmin);
      setHistory(h);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleCompanyStatusUpdate(assignmentId: number, status: CompanyStatus, notes?: string | null) {
    await updateCompanyAssignmentStatus(assignmentId, status, notes, isAdmin);

    // Every row (this one, plus any sibling row for the same company under a
    // different TM) carries its own snapshot of the full assigned_employees
    // roster — patch the matching entry everywhere it appears, or the chip for
    // this TM would keep showing the old status until an unrelated reload.
    const patchRosterEntry = (c: Company): Company =>
      c.assigned_employees?.some((e) => e.assignment_id === assignmentId)
        ? { ...c, assigned_employees: c.assigned_employees.map((e) => (e.assignment_id === assignmentId ? { ...e, status } : e)) }
        : c;

    setCompanyAssignments((prev) =>
      prev.map((c) => {
        if (c.assignment_id !== assignmentId) return patchRosterEntry(c);
        return patchRosterEntry({
          ...c,
          status,
          assignment_notes: notes !== undefined ? notes : c.assignment_notes,
          assignment_from_status: c.status ?? c.assignment_from_status,
        });
      })
    );

    try { await loadCompanyHistory(assignmentId); } catch {}
    refreshCompanyStatusCounts();
  }

  async function loadCompanyHistory(assignmentId: number) {
    setCompanyHistoryLoading(true);
    try {
      const h = await fetchCompanyAssignmentHistory(assignmentId, isAdmin);
      setCompanyHistory(h);
    } catch {
      setCompanyHistory([]);
    } finally {
      setCompanyHistoryLoading(false);
    }
  }

  return (
    <HiringContactsContext.Provider value={{
      isAdmin,
      assignments, assignmentsTotalCount, assignmentsTotalPages, assignmentsPage,
      assignmentsLoading, assignmentsLoadError, statusFilter, statusCounts, selectedManagerId,
      setStatusFilter, setSelectedManagerId, loadAssignments,
      contactsWatchFilter, setContactsWatchFilter, unassignedCount, unassignedCompaniesCount, assignedCompaniesCount,
      companies, companiesTotalCount, companiesTotalPages, companiesPage, companiesLoading,
      companiesSearchInput, setCompaniesSearchInput, handleCompaniesSearchSubmit, loadCompanies,
      companiesWatchFilter, setCompaniesWatchFilter,
      handleCompanyWatchToggle, handleCompanyPriorityChange, handleArchiveCompany, companyActionError, handleCreateCompany, companyAssignees, loadCompanyAssignees,
      handleAssignCompany, handleUnassignCompany,
      expandedCompanyId, expandedCompanyContacts, expandedCompanyContactsLoading, toggleCompanyExpand,
      contacts, contactsTotalCount, contactsTotalPages, contactsPage, contactsLoading,
      contactsSearchInput, setContactsSearchInput, handleContactsSearchSubmit,
      loadContacts, handleContactWatchToggle, handleContactPriorityChange, handleArchiveContact, contactActionError, handleCreateContact,
      assignableEmployees, contactAssignees, loadContactAssignees, handleAssign, handleUnassign,
      handleStatusUpdate, loadHistory, history, historyLoading,
      companyAssignments, companyAssignmentsTotalCount, companyAssignmentsTotalPages, companyAssignmentsPage,
      companyAssignmentsLoading, companyAssignmentsLoadError, companyStatusFilter, companyStatusCounts,
      setCompanyStatusFilter, loadCompanyAssignments, handleCompanyStatusUpdate,
      loadCompanyHistory, companyHistory, companyHistoryLoading,
      archivedCompanies, archivedCompaniesTotalCount, archivedCompaniesTotalPages, archivedCompaniesPage,
      archivedCompaniesLoading, archivedCompaniesCount, loadArchivedCompanies, handleRestoreCompany, handleDeleteCompany,
      companyCleanupModal, companyCleanupExecuting, companyCleanupError,
      openCompanyCleanup, executeCompanyCleanup, closeCompanyCleanupModal,
      archivedContacts, archivedContactsTotalCount, archivedContactsTotalPages, archivedContactsPage,
      archivedContactsLoading, archivedContactsCount, loadArchivedContacts, handleRestoreContact, handleDeleteContact,
      contactCleanupModal, contactCleanupExecuting, contactCleanupError,
      openContactCleanup, executeContactCleanup, closeContactCleanupModal,
    }}>
      {children}
    </HiringContactsContext.Provider>
  );
}
